/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Directive, Inject, NgZone, OnDestroy, WritableSignal, signal} from '@angular/core';
import {StateService, UIRouter} from '@uirouter/core';
import {TranslateService} from '@ngx-translate/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';

import {AUTH_EVENT} from '../../components/auth/auth-constants';
import {LocalDebugInfo} from '../../components/session/local-debug-info';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {UpdateSession} from '../../components/session/update-session';
import {ApiConfig} from '../config/api-config';
import {EmailHelper} from '../../components/util/email';
import {gravatarHash} from '../../components/util/gravatar-hash';
import {Uuid} from '../../components/util/uuid';
import {getRegisteredUrl} from '../config/registered-urls';
import BUILD_INFO from '../../gen-config/build-info';
import {AdnTranslateService} from '../common/filters/adn-translate.service';
import {BACKGROUNDS, SignInView, VIEWS} from './sign-in-views';
import {AdnAuthHelperService} from '../../components/util/adn-auth-helper.service';
import {AuthService} from '../../components/auth/auth.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {WebAuthnService} from '../common/controller/web-authn.service';
import {BrowserLocationService, WINDOW} from '../angular-platform-providers';
import {ExceptionHandlerService} from '../errors/exception-handler.service';
import {DocumentShellService} from '../base/document-shell.service';
import {deferSignalStateWrite} from '../common/util/template-state';
import {AppEventBusService} from '../common/events/app-event-bus.service';

function browserSupportsPasskeys(): boolean {
  return typeof globalThis !== 'undefined' && !!(globalThis as any).PublicKeyCredential;
}

function initialCredentials(): any {
  return {
    username: '',
    masqUsername: '',
    masqLast: false,
    // Seed from storage — modal CD can run before initSharedState finishes (NG0100).
    passkeyLast: !!LocalDebugInfo.obtain().getInfo('passkeyLast'),
    password: '',
    confirmPassword: '',
    twoFactorAuthCode: ''
  };
}

@Directive()
export abstract class SignInFormBase implements OnDestroy {
  private profileState?: WritableSignal<any>;
  private viewState?: WritableSignal<SignInView>;
  private credentialsState?: WritableSignal<any>;
  private errorState?: WritableSignal<any>;
  private passkeyViewState?: WritableSignal<boolean>;
  private isForced2faState?: WritableSignal<boolean>;
  private isEmail2faState?: WritableSignal<boolean>;
  private twoFactorAuthState?: WritableSignal<boolean>;
  private isSigningInState?: WritableSignal<boolean>;
  private showLoginWithPasskeyState?: WritableSignal<boolean>;

  get profile(): any { return (this.profileState ||= signal<any>(null))(); }
  set profile(value: any) { (this.profileState ||= signal<any>(null)).set(value); }
  get view(): SignInView { return (this.viewState ||= signal<SignInView>(VIEWS.SIGN_IN))(); }
  set view(value: SignInView) { (this.viewState ||= signal<SignInView>(VIEWS.SIGN_IN)).set(value); }
  get credentials(): any { return (this.credentialsState ||= signal<any>(initialCredentials()))(); }
  set credentials(value: any) { (this.credentialsState ||= signal<any>(initialCredentials())).set(value); }
  get error(): any { return (this.errorState ||= signal<any>(null))(); }
  set error(value: any) { (this.errorState ||= signal<any>(null)).set(value); }
  get passkeyView(): boolean { return (this.passkeyViewState ||= signal(false))(); }
  set passkeyView(value: boolean) { (this.passkeyViewState ||= signal(false)).set(value); }
  get isForced2fa(): boolean { return (this.isForced2faState ||= signal(false))(); }
  set isForced2fa(value: boolean) { (this.isForced2faState ||= signal(false)).set(value); }
  get isEmail2fa(): boolean { return (this.isEmail2faState ||= signal(false))(); }
  set isEmail2fa(value: boolean) { (this.isEmail2faState ||= signal(false)).set(value); }
  get twoFactorAuth(): boolean { return (this.twoFactorAuthState ||= signal(false))(); }
  set twoFactorAuth(value: boolean) { (this.twoFactorAuthState ||= signal(false)).set(value); }
  get isSigningIn(): boolean { return (this.isSigningInState ||= signal(false))(); }
  set isSigningIn(value: boolean) { (this.isSigningInState ||= signal(false)).set(value); }
  get showLoginWithPasskey(): boolean { return (this.showLoginWithPasskeyState ||= signal(browserSupportsPasskeys()))(); }
  set showLoginWithPasskey(value: boolean) { (this.showLoginWithPasskeyState ||= signal(browserSupportsPasskeys())).set(value); }
  background: string;
  frontPageContent: SafeHtml | string = '';
  frontPageTopContent: SafeHtml | string = '';
  env: string;
  releaseId = BUILD_INFO.gitSha.substring(0, 10);
  twoFactorAuthType: string;
  showMasqLink = false;
  token: string;
  regUrl: any;

  protected stateChangeListener: (() => void) | null = null;
  protected destroyed = false;
  private signingInSince = 0;
  private clearSigningInTimer: ReturnType<typeof setTimeout> | null = null;
  /** Keep the wait cursor visible long enough to notice on fast local APIs. */
  private static readonly MIN_BUSY_MS = 300;

  constructor(
    protected readonly ngZone: NgZone,
    protected readonly sanitizer: DomSanitizer,
    protected readonly adnTranslate: AdnTranslateService,
    @Inject(WINDOW) protected readonly window: any,
    @Inject(DOCUMENT) protected readonly document: any,
    protected readonly eventBus: AppEventBusService,
    @Inject(BrowserLocationService) protected readonly location: any,
    protected readonly $state: StateService,
    protected readonly uiRouter: UIRouter,
    protected readonly translate: TranslateService,
    protected readonly exceptionHandler: ExceptionHandlerService,
    @Inject(AuthService) protected readonly AuthService: any,
    @Inject(AdnAuthHelperService) protected readonly adnAuthHelper: any,
    protected readonly webAuthnResource: WebAuthnService,
    @Inject(LocalUserProfile) protected readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) protected readonly LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) protected readonly LocalUserPermissions: any,
    protected readonly documentShell: DocumentShellService,
    protected readonly apiConfig: ApiConfig,
    protected readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.clearSigningInTimer) {
      clearTimeout(this.clearSigningInTimer);
      this.clearSigningInTimer = null;
    }
    if (this.isSigningIn) {
      this.isSigningIn = false;
      this.documentShell?.setBusy(false);
    }
    if (this.stateChangeListener) {
      this.stateChangeListener();
      this.stateChangeListener = null;
    }
  }

  translateError(err: any): string {
    if (!err) {
      return '';
    }
    // Local validation messages are plaintext-only ({text}, no code).
    if (!err.code) {
      return err.text || '';
    }
    return this.adnTranslate.transform(err.text, err.code, err.parameters);
  }

  get gravatarUrl(): string {
    const hash = gravatarHash(this.credentials.username);
    return `https://secure.gravatar.com/avatar/${hash}?d=identicon`;
  }

  protected initSharedState(options?: {rejection?: any; isModal?: boolean}): void {
    this.profile = this.localUserProfile.get();
    this.view = _.get(this.$state, 'current.data.view') || VIEWS.SIGN_IN;
    if (options?.isModal) {
      this.view = VIEWS.SIGN_IN;
    }

    this.regUrl = getRegisteredUrl(this.window, this.apiConfig, this.localNetworkInfo, this.LocalNetworkProfile.get('networkId'));
    this.selectBackground();

    const debugInfo = LocalDebugInfo.obtain().getInfo();
    let masqDetails: any = {};
    if (debugInfo.lastMasqDate && moment(debugInfo.lastMasqDate).add(60, 'd').isAfter()) {
      this.showMasqLink = true;
      if (debugInfo.masqLast) {
        masqDetails = _.pick(debugInfo, ['lastMasqueraderUsername', 'lastMasqUsername', 'masqLast']);
        if (!options?.isModal && this.view.id === 'SIGN_IN'
          && (this.window.location.href.indexOf('sign-in') > -1 || this.$state.params.explicitlySignedOut)) {
          this.$state.go('sign-in.masquerade');
        }
      }
    }

    if (this.regUrl && !options?.isModal) {
      this.applyRegisteredFrontPage();
    }

    const passkeyLast = LocalDebugInfo.obtain().getInfo('passkeyLast') || false;
    this.credentials = {
      username: masqDetails.lastMasqueraderUsername
        ? masqDetails.lastMasqueraderUsername
        : (this.profile ? this.profile.username : ''),
      masqUsername: masqDetails.lastMasqueraderUsername ? masqDetails.lastMasqUsername : '',
      masqLast: masqDetails.masqLast,
      passkeyLast: passkeyLast,
      password: '',
      confirmPassword: '',
      twoFactorAuthCode: ''
    };

    if (!options?.isModal && this.view.masquerade) {
      this.credentials.username = this.$state.params.supportUsername || this.credentials.username;
      this.credentials.masqUsername = this.$state.params.masqUsername || this.credentials.masqUsername;
      this.clearMasqueradePrefillParams();
    }

    if (!options?.isModal) {
      this.appendBuildInfoScript();
      if (_.startsWith(this.location.hostname, 'localhost') && !_.startsWith(this.apiConfig.getId(), 'localhost')) {
        this.env = this.apiConfig.getId().split('_')[0].toUpperCase();
      }
      this.passwordUpdateCheck(this.$state.current);
      this.stateChangeListener = this.uiRouter.transitionService.onSuccess({}, (transition) => {
        const toState = transition.to();
        this.ngZone.run(() => {
          this.error = null;
          this.passkeyView = false;
          this.view = _.get(toState, 'data.view') || VIEWS.SIGN_IN;
          this.passwordUpdateCheck(toState);
          this.selectBackground();
          this.updatePasskeyAvailability();
        });
      }) as () => void;
    }

    this.updatePasskeyAvailability();

    if (options?.rejection && (options.rejection.code === 'error.access.token.country.mismatch'
      || options.rejection.code === 'error.refresh.token.country.mismatch')) {
      this.error = {text: 'We have detected that you are now in a different country. As a safety measure, we need you to sign in again.'};
    }
  }

  private clearMasqueradePrefillParams(): void {
    const url = new URL(this.window.location.href);
    const initialSearch = url.search;
    url.searchParams.delete('supportUsername');
    url.searchParams.delete('masqUsername');
    if (url.search !== initialSearch) {
      this.window.history.replaceState(this.window.history.state, '', url.toString());
    }
  }

  protected abstract onAuthSuccess(session: any): void;

  onSubmit(event: Event): void {
    const method = this.view.submitMethod;
    if (method === 'signIn') {
      this.signIn(this.credentials, event);
    } else if (method === 'signIn2fa') {
      this.signIn2fa(this.credentials, event);
    } else if (method === 'resetPassword') {
      this.resetPassword(this.credentials, event);
    } else if (method === 'updatePassword') {
      this.updatePassword(this.credentials, event);
    }
  }

  cancel2fa(): void {
    this.view = VIEWS.SIGN_IN;
    this.credentials.password = '';
    this.credentials.twoFactorAuthCode = '';
    this.error = null;
    this.isForced2fa = false;
    this.isEmail2fa = false;
    this.updatePasskeyAvailability();
  }

  switchToPassword(event: Event): void {
    event.preventDefault();
    this.passkeyView = false;
    this.credentials.passkeyLast = false;
  }

  changeUser(): void {
    this.profile = null;
    this.credentials.username = null;
    this.credentials.password = '';
    this.credentials.twoFactorAuthCode = '';
    this.credentials.passkeyLast = false;
    this.error = null;
    this.isForced2fa = false;
    this.isEmail2fa = false;
    if (!this.view.masquerade) {
      this.view = VIEWS.SIGN_IN;
    }
    UpdateSession.obtain().destroy();
    this.localUserProfile.destroy();
    this.LocalUserPermissions.destroy();
    this.LocalNetworkProfile.destroy();
    this.updatePasskeyAvailability();
  }

  updatePassword(credentials: any, event: Event): void {
    event.preventDefault();

    if (!credentials.password || credentials.password.length < 8) {
      this.error = {text: 'Your password needs to be at least 8 characters long'};
      return;
    }
    if (credentials.password !== credentials.confirmPassword) {
      this.error = {text: "The passwords you entered do not match. Ensure you've entered the same password both times."};
      return;
    }
    if (this.twoFactorAuth && (!credentials.twoFactorAuthCode || !credentials.twoFactorAuthCode.length)) {
      this.error = {text: 'Supply a two factor authentication code to reset your password.'};
      return;
    }

    this.setSigningIn(true);
    this.error = null;
    this.AuthService.updatePassword(credentials.password, this.token, credentials.twoFactorAuthCode).then((session: any) => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        credentials.username = session.username;
        this.doTheLogin(credentials, session);
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        if (err.status === 401) {
          this.twoFactorAuth = false;
          this.view = VIEWS.RESET_PASSWORD;
          this.error = {text: 'Your reset password link has expired. Enter your email address to generate another link.'};
          return;
        }
        if (err.status === 400 && err.data.code === 'error.auth.invalid.two.factor.auth.token') {
          this.error = {text: 'The two factor authentication code is invalid.'};
          this.twoFactorAuth = true;
          return;
        }
        const hasInvalidPassword = _.find(err.data.errors, (e: any) => {
          return e.error.code === 'validation.error.invalid.password';
        });
        if (err.status === 400 && hasInvalidPassword) {
          this.error = {text: 'The supplied password is too easily guessed and should be something less common.'};
          return;
        }
        this.error = err.data;
      });
    });
  }

  resetPassword(credentials: any, event: Event): void {
    event.preventDefault();

    if (!credentials.username || !EmailHelper.isEmail(credentials.username)) {
      this.error = {text: "Your account's email address must be entered before its password can be reset."};
      return;
    }

    this.setSigningIn(true);
    this.error = null;
    this.AuthService.resetPassword(credentials.username).then(() => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        this.view = VIEWS.PASSWORD_HAS_RESET;
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        if (err.status === 429) {
          this.view = VIEWS.PASSWORD_OVERLOAD;
          return;
        }
        if (err.status === -1) {
          this.error = {text: 'We were unable to reach the server. Please check your internet connection and try again.'};
          return;
        }
        this.error = err.data;
      });
    });
  }

  signIn2fa(credentials: any, event: Event): void {
    event.preventDefault();
    if (!credentials.twoFactorAuthCode || !credentials.twoFactorAuthCode.length) {
      this.error = {text: 'Supply a two-factor authentication code to sign in.'};
      return;
    }

    this.setSigningIn(true);
    this.error = null;
    this.AuthService.twoFactorAuth(credentials.username, credentials.twoFactorAuthCode).then((session: any) => {
      this.ngZone.run(() => {
        this.doTheLogin(credentials, session, session.masqueraderUsername);
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.handleFailedAuth(err, true, false);
      });
    });
  }

  signInWithPasskey(credentials: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.view.masquerade && credentials.masqUsername === '') {
      this.error = {text: 'Specify a masquerade user.'};
      return;
    }

    const creds: any = {};
    if (credentials.username) {
      creds.username = credentials.username;
      if (this.view.masquerade) {
        creds.masqUsername = credentials.masqUsername;
      }
    }

    this.setSigningIn(true);
    this.isForced2fa = false;
    LocalDebugInfo.obtain().setInfo('passkeyLast', false);
    this.error = null;

    this.webAuthnResource.loginWithPasskey(creds).then((session: any) => {
      this.ngZone.run(() => {
        this.localNetworkInfo.setSuppInfo('themeId', _.get(this.regUrl, ['id'], ''));
        LocalDebugInfo.obtain().setInfo('passkeyLast', true);
        this.doTheLogin(creds, session, this.view.masquerade);
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        this.eventBus.emit(AUTH_EVENT.loginFailed);
        if (err.name === 'NotAllowedError') {
          this.error = null;
        } else if (err.uiResponse) {
          this.error = err;
        } else {
          this.error = {uiResponse: 1, text: 'Passkey authentication failed'};
        }
        this.exceptionHandler.handle(err);
      });
    });
  }

  signIn(credentials: any, event: Event, isMasq?: boolean): void {
    event.preventDefault();
    LocalDebugInfo.obtain().setInfo('passkeyLast', false);

    if (!credentials.password || !credentials.password.length) {
      this.error = {text: 'Supply a password to sign in.'};
      return;
    }
    if (!credentials.username || !EmailHelper.isEmail(credentials.username)) {
      this.error = {text: "We need your account's email address before we can sign you in."};
      return;
    }

    const masqueradeAttempt = this.view.masquerade || isMasq;
    this.setSigningIn(true);
    this.isForced2fa = false;
    this.error = null;

    this.AuthService.signIn(
      credentials.username,
      credentials.password,
      masqueradeAttempt ? credentials.masqUsername : ''
    ).then((session: any) => {
      this.ngZone.run(() => {
        if (session.scope === 'TWO_FACTOR_AUTH' || session.scope === 'FORCED_TWO_FACTOR_AUTH') {
          // The auth promise can settle while Angular is verifying the modal.
          // Apply the whole view transition on the next turn so its title and
          // conditional fields cannot change between the two checks.
          deferSignalStateWrite(() => {
            this.setSigningIn(false);
            this.twoFactorAuthType = session.twoFactorAuthType;
            this.isEmail2fa = session.twoFactorAuthType === 'EMAIL';
            this.isForced2fa = session.scope === 'FORCED_TWO_FACTOR_AUTH';
            this.view = VIEWS.TWO_FACTOR_AUTH;
            this.eventBus.emit(AUTH_EVENT.login2faRequired);
          });
        } else {
          this.twoFactorAuthType = session.twoFactorAuthType;
          this.isEmail2fa = session.twoFactorAuthType === 'EMAIL';
          this.localNetworkInfo.setSuppInfo('themeId', _.get(this.regUrl, ['id'], ''));
          this.doTheLogin(credentials, session, masqueradeAttempt);
        }
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.handleFailedAuth(err, false, masqueradeAttempt);
      });
    });
  }

  protected doTheLogin(credentials: any, session: any, masqueradeAttempt?: any): void {
    this.adnAuthHelper.doAuth(this.profile, credentials, masqueradeAttempt).then(() => {
      this.ngZone.run(() => {
        // Keep the busy cursor through post-login navigation (hard assign / $state.go).
        this.twoFactorAuth = false;
        this.isForced2fa = false;
        this.onAuthSuccess(session);
      });
    }, (err: any) => {
      this.ngZone.run(() => {
        this.setSigningIn(false);
        if (err && err.status) {
          this.handleFailedAuth(err, false, false);
        } else {
          this.error = {
            text: 'Sign-in succeeded but the application could not finish loading your profile. Please try again.'
          };
        }
      });
    });
  }


  protected setSigningIn(signingIn: boolean): void {
    if (this.clearSigningInTimer) {
      clearTimeout(this.clearSigningInTimer);
      this.clearSigningInTimer = null;
    }
    if (signingIn) {
      this.isSigningIn = true;
      this.signingInSince = Date.now();
      this.documentShell?.setBusy(true);
      return;
    }
    const remain = Math.max(0, SignInFormBase.MIN_BUSY_MS - (Date.now() - this.signingInSince));
    const clear = () => {
      this.clearSigningInTimer = null;
      if (this.destroyed) {
        return;
      }
      this.isSigningIn = false;
      this.documentShell?.setBusy(false);
      // NgZone.run alone does not refresh zoneless CD; without this the Sign in
      // spinner sticks and alert-danger stays hidden (error && !isSigningIn).
    };
    if (remain > 0) {
      // Keep isSigningIn true (disabled button + #app.loading) for the minimum window.
      this.clearSigningInTimer = setTimeout(() => this.ngZone.run(clear), remain);
    } else {
      clear();
    }
  }

  protected handleFailedAuth(err: any, isTwoFactor: boolean, isMasquerade: boolean): void {
    this.setSigningIn(false);

    const errorCode = err?.data?.code;
    const hasMessage = !!(errorCode || err?.data?.text);
    const noResponse = !err || err.status === -1 || err.status === 0 || !hasMessage;

    if (err?.status === 400 && errorCode === 'error.account.locked') {
      this.view = VIEWS.PASSWORD_HAS_RESET;
      this.error = {text: 'Your account has been locked due to too many failed login attempts. You have been sent an email with which you can reset your password and unlock your account.'};
      return;
    }
    if (err?.status === 400 && errorCode === 'error.password.expired') {
      this.view = VIEWS.PASSWORD_NEEDS_RESET;
      this.error = {text: 'We have updated our security protocols and, as part of this, require users to periodically change their password. You will need to set a new password before you can proceed with using the system.'};
      return;
    }

    this.eventBus.emit(AUTH_EVENT.loginFailed);

    if (err?.status === 400 && isMasquerade && errorCode === 'error.user.missing.two.factor.auth') {
      this.error = {text: 'Your account is not setup for 2FA. You require 2FA to masquerade as this user.'};
    } else if (err?.status === 400 && errorCode === 'error.auth.forced.two.factor.auth.required') {
      this.error = {text: 'We have detected that you are signing in from a different country. As a safety measure, we have sent you a code via email to enter to confirm that it\'s you.'};
    } else if (noResponse) {
      this.error = {text: 'We were unable to reach the server. Please check your internet connection and try again.'};
    } else {
      this.error = err.data;
    }

    if (isTwoFactor && this.error?.code === 'error.auth.failed') {
      this.error.code = 'error.auth.failed.2fa';
    }
  }

  private selectBackground(): void {
    const registeredBackgrounds = _.isArray(_.get(this.regUrl, 'bgs'))
      ? this.regUrl.bgs
      : BACKGROUNDS.standard;
    const backgrounds = this.view.masquerade
      ? BACKGROUNDS.masquerade
      : registeredBackgrounds.filter((background: string) => background !== 'nz');
    const availableBackgrounds = backgrounds.length ? backgrounds : BACKGROUNDS.standard;
    this.background = availableBackgrounds[Math.floor(Math.random() * availableBackgrounds.length)];
  }

  private passwordUpdateCheck(theState: any): void {
    if (theState.name !== 'sign-in.password-reset') {
      return;
    }
    const token = _.get(this.location.queryParams, 'token');
    if (!token) {
      return;
    }
    if (!_.isString(token) || token.length < 20) {
      this.error = {text: 'It looks like you clicked on an invalid or expired password reset link. Please try resetting your password again.'};
      return;
    }
    const twoFactorAuth = _.get(this.location.queryParams, '2fa');
    this.twoFactorAuth = twoFactorAuth && twoFactorAuth === 'true';
    this.showMasqLink = false;
    this.token = token;
    this.view = VIEWS.PASSWORD_UPDATE;
  }

  private updatePasskeyAvailability(): void {
    const next = !!(this.window.PublicKeyCredential
      && (this.view.id === 'SIGN_IN' || this.view.id === 'MASQUERADE'));
    if (next === this.showLoginWithPasskey) {
      return;
    }
    // Defer flips — SignInModal *ngIf="showLoginWithPasskey" was NG0100 false→true
    // inside the passkeyLast block when availability landed mid-verify.
    deferSignalStateWrite(() => {
      this.showLoginWithPasskey = next;
    });
  }

  private applyRegisteredFrontPage(): void {
    const frontPageTranslationKey = this.regUrl.id + '.frontPage';
    let frontPageContent = this.translate.instant(frontPageTranslationKey);
    if (frontPageTranslationKey === frontPageContent) {
      frontPageContent = '';
    }
    this.frontPageContent = frontPageContent
      ? this.sanitizer.bypassSecurityTrustHtml(frontPageContent)
      : '';

    const frontPageTopTranslationKey = this.regUrl.id + '.frontPageTopContent';
    let frontPageTopContent = this.translate.instant(frontPageTopTranslationKey);
    if (frontPageTopTranslationKey === frontPageTopContent) {
      frontPageTopContent = '';
    }
    this.frontPageTopContent = frontPageTopContent
      ? this.sanitizer.bypassSecurityTrustHtml(frontPageTopContent)
      : '';

    if (this.regUrl.favicon) {
      const favicon = this.document.getElementById('favicon');
      if (favicon) {
        favicon.href = this.regUrl.favicon.href;
      }
    }
  }

  private appendBuildInfoScript(): void {
    const script = this.document.createElement('script');
    script.setAttribute('src', 'current-build-info.js?cb=' + Uuid.generate());
    const bodyElement = this.document.body;
    if (bodyElement) {
      bodyElement.appendChild(script);
    }
  }
}
